import { useState } from "react";
import { useForm } from "react-hook-form";
import { Shield } from "lucide-react";
import * as accountApi from "../../../api/account/api";
import { AuthStatus } from "../../../types/domain";
import { BackToLoginLink } from "../components/BackToLoginLink";
import { ErrorAlert } from "../components/ErrorAlert";
import { SubmitButton } from "../components/SubmitButton";
import { LoginFormField } from "../components/LoginFormField";
import { PageLoader } from "../components/PageLoader";
import { useApiError } from "../hooks/useApiError";
import { LoginViewState } from "../types";
import { safeRedirectPath } from "../utils/validators";
import { getBaseUtcOffset } from "../utils/timezone";
import { ViewHeading } from "../components/ViewHeading";
import { FormOverlay } from "@/components/common/Loading";

type Props = {
  onViewChange: (view: LoginViewState, extra?: Record<string, string>) => void;
};

type VerifyCodeFormValues = {
  code: string;
  rememberBrowser: boolean;
};

export const VerifyCodeView = ({ onViewChange }: Props) => {
  const { apiError, setApiError, clearError } = useApiError();
  const [redirecting, setRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyCodeFormValues>({
    defaultValues: { code: "", rememberBrowser: false },
  });

  const onSubmit = async (data: VerifyCodeFormValues) => {
    clearError();
    try {
      const result = await accountApi.verifyCode({
        code: data.code.trim(),
        timezone: getBaseUtcOffset(),
        rememberBrowser: data.rememberBrowser,
        returnUrl: new URLSearchParams(window.location.search).get("ReturnUrl") ?? "",
      });

      switch (result.status) {
        case AuthStatus.Success:
          setRedirecting(true);
          window.location.href = safeRedirectPath(result.redirectUrl);
          break;
        case AuthStatus.RequiresPasswordUpdate:
          onViewChange("updatePassword", {
            passwordDays: String(result.passwordDays ?? 0),
            passwordStatus: result.passwordStatus ?? "",
          });
          break;
        case AuthStatus.Error:
        default:
          setApiError(result.errorMessage ?? "Invalid code. Please try again.");
      }
    } catch {
      setApiError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <>
      {redirecting && <PageLoader />}

      <FormOverlay loading={isSubmitting || redirecting}>

      <ViewHeading
        title="Authorisation Code"
        subtitle="Your account is set up with additional security measures. A code has been sent to the email
        address registered to you within Klipboard CRM."
      />

      {apiError && <ErrorAlert message={apiError} />}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LoginFormField
          label="Verification code"
          placeholder="Enter code"
          autoComplete="one-time-code"
          inputMode="numeric"
          error={errors.code?.message}
          disabled={isSubmitting}
          icon={<Shield size={16} color="#9CA3AF" strokeWidth={2} />}
          {...register("code", { required: "Verification code is required" })}
        />

        {/* Remember browser */}
        <label className="flex items-center gap-2 mb-5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-[#E5E7EB] accent-brand-pink"
            disabled={isSubmitting}
            {...register("rememberBrowser")}
          />
          <span className="text-[0.8125rem] text-[#374151]">Remember me in this browser?</span>
        </label>

        <div className="mb-3">
          <SubmitButton loading={isSubmitting}>Authorise</SubmitButton>
        </div>
      </form>

      <BackToLoginLink onClick={() => onViewChange("login")} />
      </FormOverlay>
    </>
  );
};
